// ChangeSignerEmailDialog — send a pending signature request to a
// different address for the same person.
//
// CONSUMER: internal-ui.
//
// Nate Swartz, 2026-09-19: "the option needs to be present to resend to
// a signer during the esign process in case it was buried and they
// cannot find it or to send to another email entirely… I have clients
// that have partially signed and need to send it to a different email."
// Before this the only ways out were cancelling the whole request and
// starting over — throwing away signatures already collected — or
// copying the link and delivering it by hand.
//
// Lives in one component because two screens ask the same question:
// the "Sent for signature" panel on a record (where Nate was) and the
// E-sign envelope page. Same dialog, same endpoint, same refusals.

import { useEffect, useState } from "react";

import { apiV11, ApiError } from "../../api";
import { Button, ErrorBanner, FormField } from "../../ui";
import { copyTextToClipboard } from "./copy-to-clipboard";
import { Modal } from "../Modal";
import { useToast } from "../Toast";

export interface ChangeSignerEmailTarget {
  envelopeId: string;
  signerId: string;
  name: string;
  email: string;
}

export interface ChangeSignerEmailDialogProps {
  /** The signer being moved; null closes the dialog. */
  target: ChangeSignerEmailTarget | null;
  onClose: () => void;
  /** Reload the surface underneath — the address on screen has changed. */
  onChanged: () => void | Promise<void>;
  /** Where a link goes when the email couldn't leave (Outlook not
   *  connected). The caller renders it with a copy button, same as a
   *  resend's fallback. Optional: the dialog shows its own copy button
   *  too, so a caller that has nowhere to put one loses nothing. */
  onFallbackLink?: (link: { name: string; url: string }) => void;
}

export function ChangeSignerEmailDialog({
  target,
  onClose,
  onChanged,
  onFallbackLink,
}: ChangeSignerEmailDialogProps) {
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Held here as well as handed to the caller: when the invitation
  // can't be emailed, the fresh link IS the deliverable, and a dialog
  // that closes on "copy it from somewhere" is a dead end.
  const [fallback, setFallback] = useState<string | null>(null);

  // Start each open from the signer's current details rather than
  // whatever the last one left behind — a stale address in the box is
  // how the wrong person gets a contract.
  useEffect(() => {
    if (!target) return;
    setEmail("");
    setName(target.name);
    setReason("");
    setError(null);
    setFallback(null);
  }, [target]);

  if (!target) return null;

  async function submit() {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiV11.changeEsignSignerEmail(target.envelopeId, target.signerId, {
        email: email.trim(),
        ...(name.trim() && name.trim() !== target.name ? { name: name.trim() } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      await onChanged();
      if (!res.invited) {
        // A later turn under signing order, or a copy recipient. The
        // address is fixed; nothing goes out yet, and saying so is
        // kinder than letting the advisor believe an email left.
        toast.success(`${res.name}'s address is updated. They'll be emailed when it's their turn.`);
        onClose();
      } else if (res.emailed) {
        toast.success(`Sent to ${res.email}. The old link no longer works.`);
        onClose();
      } else if (res.sign_url) {
        toast.success("Address updated — the email couldn't go out, so copy the link and send it.");
        setFallback(res.sign_url);
        onFallbackLink?.({ name: res.name, url: res.sign_url });
      } else {
        toast.success(`${res.name}'s address is updated.`);
        onClose();
      }
    } catch (err) {
      // Every refusal the server makes is a sentence worth reading — a
      // signer who already signed, an address another signer holds, a
      // document that has closed. Shown in the dialog, next to the box
      // the advisor would change, rather than as a toast that vanishes.
      setError(err instanceof ApiError ? err.message : "Couldn't change that address.");
    } finally {
      setBusy(false);
    }
  }

  const ready = email.trim() !== "" && email.trim().toLowerCase() !== target.email.toLowerCase();

  return (
    <Modal
      open
      onClose={onClose}
      title={`Send to a different email for ${target.name}`}
      className="modal-narrow"
      footer={
        fallback ? (
          <Button variant="sw-primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              Never mind
            </Button>
            <Button variant="sw-primary" disabled={busy || !ready} onClick={() => void submit()}>
              {busy ? "Sending…" : "Send to this address"}
            </Button>
          </>
        )
      }
    >
      {fallback ? (
        <>
          <p className="esign-prep-hint">
            The address is changed and the old link is dead — but the email couldn't go out from
            your Outlook. Copy the new link and send it however you like.
          </p>
          <div className="esign-envelope-fallback-link">
            <code>{fallback}</code>
            <Button
              variant="sw-default"
              size="sm"
              onClick={() => {
                void copyTextToClipboard(fallback).then((ok) => {
                  if (ok) toast.success("Link copied.");
                  else toast.error("Couldn't copy — select the link text and copy it.");
                });
              }}
            >
              Copy link
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="esign-prep-hint">
            Currently going to <strong>{target.email}</strong>. A new link goes to the address you
            enter, and the link they already have stops working. Anything already signed on this
            document stays signed.
          </p>
          {error ? <ErrorBanner message={error} /> : null}
          <FormField
            label="New email address"
            type="email"
            autoComplete="off"
            value={email}
            placeholder="them@example.com"
            onChange={(e) => setEmail(e.currentTarget.value)}
          />
          <FormField
            label="Their name"
            value={name}
            hint="Only change this to fix a spelling — it's the same person signing."
            onChange={(e) => setName(e.currentTarget.value)}
          />
          <FormField
            label="Why (optional)"
            value={reason}
            placeholder="Work address bounces"
            hint="Recorded on the document's trail."
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <p className="esign-prep-hint">
            This doesn't change their contact record — it only changes where this one document
            goes.
          </p>
        </>
      )}
    </Modal>
  );
}
